import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { 
  BarChart3, Users, CheckCircle, XCircle, TrendingUp,
  FileCheck, Shield, Beaker, ChevronDown, RefreshCw
} from "lucide-react";

/**
 * Analytics Dashboard Component
 * Shows consent statistics, cohort analytics, and symptom prevalence
 */
export default function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [consentStats, setConsentStats] = useState(null);
  const [cohortData, setCohortData] = useState(null);
  const [symptomData, setSymptomData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [selectedCohortGroup, setSelectedCohortGroup] = useState("cancer_type");
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    fetchAllData();
  }, [selectedDays, selectedCohortGroup]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [consent, cohort, symptoms, overview] = await Promise.all([
        api.get(`/analytics/consents/stats?days=${selectedDays}`),
        api.get(`/analytics/cohorts?group_by=${selectedCohortGroup}&days=${selectedDays}`),
        api.get(`/analytics/symptoms/prevalence?days=${selectedDays}`),
        api.get(`/analytics/dashboard/overview?days=${selectedDays}`)
      ]);
      setConsentStats(consent.data);
      setCohortData(cohort.data);
      setSymptomData(symptoms.data);
      setOverviewData(overview.data);
    } catch (err) {
      toast.error("Erreur chargement analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Analytics & Statistiques</h2>
        <div className="flex items-center gap-3">
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
          <button
            onClick={fetchAllData}
            className="p-2 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {overviewData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="Patients actifs"
            value={overviewData.patients?.active || 0}
            subtext={`sur ${overviewData.patients?.total || 0} total`}
            color="teal"
          />
          <StatCard
            icon={<CheckCircle className="w-5 h-5" />}
            label="Taux de check-in"
            value={`${overviewData.compliance?.checkin_rate || 0}%`}
            subtext={`${overviewData.compliance?.patients_reporting || 0} patients`}
            color="green"
          />
          <StatCard
            icon={<BarChart3 className="w-5 h-5" />}
            label="Alertes résolues"
            value={`${overviewData.alerts?.resolution_rate || 0}%`}
            subtext={`${overviewData.alerts?.resolved || 0}/${overviewData.alerts?.total || 0}`}
            color="blue"
          />
          <StatCard
            icon={<Shield className="w-5 h-5" />}
            label="Évaluations complètes"
            value={`${overviewData.patients?.evaluation_rate || 0}%`}
            subtext="des patients"
            color="purple"
          />
        </div>
      )}

      {/* Consent Statistics */}
      {consentStats && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileCheck className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold text-slate-800">Statistiques de Consentement</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Overall stats */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-2">Taux global d'acceptation</p>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-teal-600">
                  {consentStats.totals?.acceptance_rate || 0}%
                </span>
                <span className="text-sm text-slate-500 mb-1">
                  ({consentStats.totals?.consents_accepted || 0}/{consentStats.totals?.total_users || 0})
                </span>
              </div>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-500 rounded-full" 
                  style={{width: `${consentStats.totals?.acceptance_rate || 0}%`}}
                />
              </div>
            </div>

            {/* Research consent */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-2">Opt-in Recherche médicale</p>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-purple-600">
                  {consentStats.totals?.research_opt_in_rate || 0}%
                </span>
                <span className="text-sm text-slate-500 mb-1">
                  ({consentStats.totals?.research_opt_in || 0} utilisateurs)
                </span>
              </div>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 rounded-full" 
                  style={{width: `${consentStats.totals?.research_opt_in_rate || 0}%`}}
                />
              </div>
            </div>

            {/* Pending consents */}
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-sm text-amber-700 mb-2">Consentements en attente</p>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-amber-600">
                  {consentStats.totals?.consents_pending || 0}
                </span>
                <span className="text-sm text-amber-600 mb-1">
                  utilisateurs
                </span>
              </div>
            </div>
          </div>

          {/* By role breakdown */}
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-600 mb-3">Par rôle</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(consentStats.by_role || {}).map(([role, data]) => (
                <div key={role} className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">{formatRoleName(role)}</p>
                  <p className="text-lg font-bold text-slate-700">{data.total}</p>
                  <div className="flex justify-center gap-2 mt-1 text-xs">
                    <span className="text-green-600">{data.acceptance_rate}%</span>
                    <span className="text-purple-600">{data.research_opt_in_rate}% R</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cohort Analytics */}
      {cohortData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Beaker className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-slate-800">Analyse par Cohorte</h3>
            </div>
            <select
              value={selectedCohortGroup}
              onChange={(e) => setSelectedCohortGroup(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="cancer_type">Type de cancer</option>
              <option value="treatment_type">Type de traitement</option>
              <option value="therapeutic_line">Ligne thérapeutique</option>
              <option value="age_group">Tranche d'âge</option>
              <option value="status">Statut</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Cohorte</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Patients</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Actifs</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Critiques</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Âge moyen</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Évaluation</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Recherche</th>
                </tr>
              </thead>
              <tbody>
                {cohortData.cohorts?.map((cohort, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-800">{cohort.cohort}</td>
                    <td className="text-right py-3 px-4">{cohort.patient_count}</td>
                    <td className="text-right py-3 px-4 text-green-600">{cohort.active_patients}</td>
                    <td className="text-right py-3 px-4 text-red-600">{cohort.critical_patients}</td>
                    <td className="text-right py-3 px-4">{cohort.average_age} ans</td>
                    <td className="text-right py-3 px-4">{cohort.evaluation_rate}%</td>
                    <td className="text-right py-3 px-4 text-purple-600">{cohort.research_consent_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Symptom Prevalence */}
      {symptomData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-slate-800">Prévalence des Symptômes</h3>
            <span className="text-sm text-slate-500 ml-2">
              (sur {symptomData.total_checkins} check-ins)
            </span>
          </div>

          <div className="space-y-3">
            {symptomData.symptoms?.slice(0, 10).map((symptom, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-40">
                  <p className="text-sm font-medium text-slate-700">{symptom.display_name}</p>
                  {symptom.meddra_code && (
                    <p className="text-xs text-slate-400">MedDRA: {symptom.meddra_code}</p>
                  )}
                </div>
                <div className="flex-1">
                  <div className="h-6 bg-slate-100 rounded-full overflow-hidden flex">
                    <div 
                      className="h-full bg-green-400" 
                      style={{width: `${(symptom.severity_distribution?.mild / symptom.occurrences * 100) || 0}%`}}
                      title="Léger"
                    />
                    <div 
                      className="h-full bg-amber-400" 
                      style={{width: `${(symptom.severity_distribution?.moderate / symptom.occurrences * 100) || 0}%`}}
                      title="Modéré"
                    />
                    <div 
                      className="h-full bg-red-400" 
                      style={{width: `${(symptom.severity_distribution?.severe / symptom.occurrences * 100) || 0}%`}}
                      title="Sévère"
                    />
                  </div>
                </div>
                <div className="w-24 text-right">
                  <p className="text-sm font-bold text-slate-700">{symptom.prevalence_pct}%</p>
                  <p className="text-xs text-slate-500">{symptom.occurrences} cas</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded" /> Léger</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 rounded" /> Modéré</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded" /> Sévère</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, subtext, color }) {
  const colorClasses = {
    teal: "bg-teal-50 text-teal-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    amber: "bg-amber-50 text-amber-600"
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className={`inline-flex p-2 rounded-lg ${colorClasses[color] || colorClasses.teal}`}>
        {icon}
      </div>
      <p className="text-sm text-slate-500 mt-3">{label}</p>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-400">{subtext}</p>
    </div>
  );
}

function formatRoleName(role) {
  const names = {
    SUPER_ADMIN_SAAS: "Super Admin",
    ADMIN_CLINIQUE: "Admin",
    MEDECIN: "Médecin",
    IDE: "Infirmière",
    PATIENT: "Patient",
    SECRETAIRE_MEDICAL: "Secrétariat",
  };
  return names[role] || role;
}
